// src/Dashboard/pages/teacher/AttendanceTable/AttendanceTableDashboard.jsx

import React, { useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";

import { useAttendanceTableDashboard } from "./hooks/useAttendanceTableDashboard";

import AttendanceTableHeader from "./components/AttendanceTableHeader";
import AttendanceGroupPickerPanel from "./components/AttendanceGroupPickerPanel";
import AttendanceMonthToolbar from "./components/AttendanceMonthToolbar";
import AttendanceKpiCards from "./components/AttendanceKpiCards";
import AttendanceLegend from "./components/AttendanceLegend";
import AttendanceHeatmapTable from "./components/AttendanceHeatmapTable";
import AttendanceMobileCards from "./components/AttendanceMobileCards";
import AttendanceChartsPanel from "./components/AttendanceChartsPanel";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getRoleId(user, storedUser) {
  return Number(
    user?.role_id ||
      user?.roleId ||
      storedUser?.role_id ||
      storedUser?.roleId ||
      0,
  );
}

function UnauthorizedPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Tableau des présences
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs et enseignants.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AttendanceTableDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getRoleId(user, storedUser);

  const authorized = roleId === 1 || roleId === 3;

  const [showCharts, setShowCharts] = useState(false);

  const {
    groups,
    months,
    selectedGroupId,
    selectedGroup,
    selectedMonth,
    adherents,
    tableRows,
    allAttendances,
    seasonActive,
    loading,
    loadingGroups,
    loadingAdherents,
    kpis,
    highlightIds,
    setSelectedGroupId,
    setSelectedMonth,
    handleToggleCell,
    refresh,
    setAllAttendances,
  } = useAttendanceTableDashboard();

  if (!authorized) {
    return <UnauthorizedPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <AttendanceTableHeader
          loading={loading || loadingGroups}
          onRefresh={refresh}
        />

        {!selectedGroupId ? (
          <AttendanceGroupPickerPanel
            groups={groups}
            loading={loadingGroups}
            selectedGroupId={selectedGroupId}
            onSelectGroup={setSelectedGroupId}
          />
        ) : (
          <>
            <AttendanceMonthToolbar
              groups={groups}
              months={months}
              selectedGroup={selectedGroup}
              selectedGroupId={selectedGroupId}
              selectedMonth={selectedMonth}
              loading={loading}
              onSelectGroup={setSelectedGroupId}
              onSelectMonth={setSelectedMonth}
              onShowGroupPicker={() => setSelectedGroupId("")}
              showCharts={showCharts}
              onToggleCharts={() => setShowCharts((value) => !value)}
            />

            <AttendanceKpiCards
              kpis={kpis}
              adherentsCount={adherents.length}
              loading={loading || loadingAdherents}
            />

            {showCharts && (
              <AttendanceChartsPanel
                seasonActive={seasonActive}
                selectedGroup={selectedGroup}
                adherents={adherents}
                allAttendances={allAttendances}
                setAllAttendances={setAllAttendances}
              />
            )}

            <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

              <div className="p-4 sm:p-5 lg:p-6">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                      Tableau mensuel
                    </p>

                    <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                      Présences et absences du groupe
                    </h2>

                    <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                      Cliquez une case pour basculer : non saisi → présent →
                      absent → non saisi. La mise à jour est immédiate.
                    </p>
                  </div>

                  <AttendanceLegend />
                </div>

                {loading || loadingAdherents ? (
                  <div className="mt-5 space-y-3">
                    {[...Array(6)].map((_, index) => (
                      <div
                        key={index}
                        className="h-16 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                      />
                    ))}
                  </div>
                ) : tableRows.length === 0 ? (
                  <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-800/40">
                    <h3 className="text-base font-bold text-slate-900 dark:text-white">
                      Aucun résultat à afficher
                    </h3>

                    <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                      Sélectionnez un mois ou vérifiez que le groupe contient
                      bien des adhérents.
                    </p>
                  </div>
                ) : (
                  <div className="mt-5">
                    <AttendanceHeatmapTable
                      month={selectedMonth}
                      rows={tableRows}
                      highlightIds={highlightIds}
                      onToggleCell={handleToggleCell}
                    />

                    <AttendanceMobileCards
                      month={selectedMonth}
                      rows={tableRows}
                      highlightIds={highlightIds}
                      onToggleCell={handleToggleCell}
                    />
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>

      <ToastContainer />
    </section>
  );
}
